/**
 * Module 57 — "has the player touched this document yet?", observed from the
 * earliest possible moment.
 *
 * Browsers refuse to start audio before a gesture, so `AudioService` holds music
 * until one happens. Until this class existed, the gesture was observed by
 * listeners registered inside the composition roots — which, in the CrazyGames
 * embed, run behind `await ensureCrazyGamesSdkReady()` (an `SDK.init()` round
 * trip plus a poll, bounded at 10 s) and `await i18n.changeLanguage()`. For that
 * whole window the document was blank and unlistened, so a player who clicked
 * during loading was never seen, and the music stayed owed until they touched
 * the board. That is the bug this class removes: the entry installs it before
 * any `await`, so there is no window left to miss.
 *
 * A class rather than module state, and with no side effects at import, for two
 * reasons: `packages/audio` is framework code, where a global singleton is
 * against the repo's dependency rule; and a test needs to be able to have its
 * own, un-activated, without the previous test's gesture leaking into it.
 *
 * It answers only "has it happened", never "is audio playable" — the audio
 * context's own state is the adapter's business (`HowlerAdapter.resume`).
 */
/** The gestures a browser accepts as activation, and that reach `document`. */
const ACTIVATION_EVENTS = ['pointerdown', 'keydown', 'touchstart'];
export class UserActivation {
    constructor() {
        this._activated = false;
        this._waiting = new Set();
        this._detach = null;
    }
    get hasActivated() {
        return this._activated;
    }
    /**
     * Start watching. Idempotent, and safe where there is no DOM (tests, SSR), so
     * an entry can call it unconditionally as its first statement.
     *
     * Capture phase, so a handler calling `stopPropagation` cannot swallow it;
     * passive, so it can never delay a scroll; and NOT `{ once: true }` on the
     * listener itself — `mark()` is what makes it one-shot, and it detaches all
     * three together so a keydown does not leave two pointer listeners live.
     */
    install(target = globalThis.document) {
        if (this._detach || this._activated || !target)
            return;
        const onGesture = () => this.mark();
        for (const evt of ACTIVATION_EVENTS) {
            target.addEventListener(evt, onGesture, { passive: true, capture: true });
        }
        this._detach = () => {
            for (const evt of ACTIVATION_EVENTS) {
                target.removeEventListener(evt, onGesture, { capture: true });
            }
        };
    }
    /**
     * Record activation and tell everyone waiting. Public because the platform
     * may know before the DOM does — a host SDK reporting that its own splash was
     * clicked is a real gesture, in the same document, that our listeners were not
     * yet alive to see.
     */
    mark() {
        if (this._activated)
            return;
        this._activated = true;
        this._detach?.();
        this._detach = null;
        const waiting = [...this._waiting];
        this._waiting.clear();
        for (const fn of waiting)
            fn();
    }
    onActivated(fn) {
        if (this._activated) {
            fn();
            return;
        }
        this._waiting.add(fn);
    }
}
